import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Server, Settings, Shield, Zap, Clock, AlertCircle, Plus, Database, Eye, EyeOff, Edit, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import toast from 'react-hot-toast';
import { APIProvider, AuthConfig, EndpointParameter } from '@/services/apiProviderService';



interface APIProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (provider: APIProvider) => Promise<void>;
  provider?: APIProvider;
  isLoading?: boolean;
}

const defaultProvider: APIProvider = {
  name: '',
  description: '',
  baseUrl: '',
  documentation: '',
  requiresAuth: false,
  authConfigs: [],
  rateLimit: 1000,
  timeout: 10000,
  isActive: true,
  endpoints: []
};

export default function APIProviderModal({
  isOpen,
  onClose,
  onSubmit,
  provider,
  isLoading = false,
}: APIProviderModalProps) {
  const [formData, setFormData] = useState<APIProvider>(defaultProvider);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [endpointPath, setEndpointPath] = useState('');
  const [endpointMethod, setEndpointMethod] = useState<'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'>('GET');
  const [endpointBodyType, setEndpointBodyType] = useState<'json' | 'x-www-form-urlencoded' | 'form-data' | 'none'>('json');
  const [endpointDescription, setEndpointDescription] = useState('');
  const [showValueMap, setShowValueMap] = useState<Record<string, boolean>>({});
  const [editingEndpointIndex, setEditingEndpointIndex] = useState<number | null>(null);
  const [endpointParameters, setEndpointParameters] = useState<EndpointParameter[]>([]);
  const [showParamsForm, setShowParamsForm] = useState(false);
  const [paramName, setParamName] = useState('');
  const [paramType, setParamType] = useState<'string' | 'number' | 'boolean'>('string');
  const [paramLocation, setParamLocation] = useState<'query' | 'body' | 'header' | 'path'>('query');
  const [paramRequired, setParamRequired] = useState(false);
  const [paramDescription, setParamDescription] = useState('');
  const [editingParamIndex, setEditingParamIndex] = useState<number | null>(null);

  const toggleShowValue = (key: string) => {
    setShowValueMap(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const startEditParam = (index: number) => {
    const p = endpointParameters[index];
    if (!p) return;
    setParamName(p.name);
    setParamType(p.type || 'string');
    setParamLocation(p.location || (['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? 'body' : 'query'));
    setParamRequired(p.required);
    setParamDescription(p.description || '');
    setEditingParamIndex(index);
  };

  const cancelEditParam = () => {
    setParamName('');
    setParamType('string');
    setParamLocation(['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? 'body' : 'query');
    setParamRequired(false);
    setParamDescription('');
    setEditingParamIndex(null);
  };

  const addOrUpdateParam = () => {
    if (!paramName.trim()) {
      toast.error('Silakan isi nama parameter');
      return;
    }

    const newParam: EndpointParameter = {
      name: paramName.trim(),
      type: paramType,
      location: paramLocation,
      required: paramRequired,
      description: paramDescription.trim()
    };

    if (editingParamIndex !== null) {
      setEndpointParameters(prev => prev.map((p, i) => i === editingParamIndex ? newParam : p));
      toast.success('Parameter diperbarui');
      setEditingParamIndex(null);
    } else {
      setEndpointParameters(prev => [...prev, newParam]);
    }

    setParamName('');
    setParamType('string');
    setParamLocation(['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? 'body' : 'query');
    setParamRequired(false);
    setParamDescription('');
  };

  const removeParam = (index: number) => {
    if (editingParamIndex === index) {
      cancelEditParam();
    } else if (editingParamIndex !== null && index < editingParamIndex) {
      setEditingParamIndex(prev => (prev !== null ? prev - 1 : null));
    }
    setEndpointParameters(prev => prev.filter((_, i) => i !== index));
  };

  const startEditEndpoint = (index: number) => {
    const ep = formData.endpoints[index];
    if (!ep) return;
    setEndpointMethod(ep.method);
    setEndpointBodyType(ep.bodyType || (['POST', 'PUT', 'PATCH'].includes(ep.method) ? 'json' : 'none'));
    setEndpointPath(ep.path);
    setEndpointDescription(ep.description || '');
    const params = Array.isArray(ep.parameters) ? (ep.parameters as EndpointParameter[]) : [];
    setEndpointParameters(params);
    setShowParamsForm(params.length > 0);
    cancelEditParam();
    setEditingEndpointIndex(index);
  };

  const cancelEditEndpoint = () => {
    setEndpointMethod('GET');
    setEndpointBodyType('json');
    setEndpointPath('');
    setEndpointDescription('');
    setEndpointParameters([]);
    setShowParamsForm(false);
    cancelEditParam();
    setEditingEndpointIndex(null);
  };

  const isEdit = !!provider?.id;

  useEffect(() => {
    if (provider) {
      let authConfigs = provider.authConfigs || [];
      if ((!authConfigs || authConfigs.length === 0) && provider.authConfig) {
        const cfg = provider.authConfig;
        if (cfg.authConfigs && Array.isArray(cfg.authConfigs) && cfg.authConfigs.length > 0) {
          authConfigs = cfg.authConfigs;
        } else if ((cfg as any).type === 'custom' || ((cfg as any).customHeaders && Array.isArray((cfg as any).customHeaders) && (cfg as any).customHeaders.length > 0)) {
          authConfigs = [{
            type: 'custom',
            customHeaders: (cfg as any).customHeaders || []
          }];
        } else if ((cfg as any).headerName) {
          const authType = (provider.authType as AuthConfig['type']) || ((cfg as any).type as AuthConfig['type']) || 'api_key';
          authConfigs = [{
            type: authType,
            headerName: cfg.headerName || '',
            headerValue: cfg.headerValue || '',
            username: cfg.username || '',
            password: cfg.password || '',
            clientId: cfg.clientId || '',
            clientSecret: cfg.clientSecret || '',
            tokenUrl: cfg.tokenUrl || '',
            customHeaders: cfg.customHeaders || []
          }];
          if (cfg.additionalHeaders) {
            Object.entries(cfg.additionalHeaders).forEach(([name, value]) => {
              if (name !== cfg.headerName) {
                authConfigs.push({
                  type: 'api_key',
                  headerName: name,
                  headerValue: value
                });
              }
            });
          }
        }
      }
      setFormData({
        ...provider,
        authConfigs
      });
    } else {
      setFormData(defaultProvider);
    }
    setErrors({});
  }, [provider, isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Basic required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Provider name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Base URL is required';
    } else if (!isValidUrl(formData.baseUrl)) {
      newErrors.baseUrl = 'Please enter a valid URL (e.g., https://api.example.com)';
    }

    // Only validate auth configs if user has enabled authentication and filled all basic info
    if (formData.requiresAuth && (!formData.authConfigs || formData.authConfigs.length === 0)) {
      // Only show error if user has filled all basic info and is ready to submit
      if (formData.name.trim() && 
          formData.description.trim() && 
          formData.baseUrl.trim() && 
          isValidUrl(formData.baseUrl)) {
        newErrors.auth = 'At least one authentication method is required when authentication is enabled';
      }
    }

    // Validate each auth config - only validate if user has started filling the fields
    (formData.authConfigs || []).forEach((authConfig, index) => {
      // Only validate if user has started filling any field in this auth config
      const hasCustomHeaders = (authConfig.customHeaders || []).some(
        (header) => header.name?.trim() || header.value?.trim()
      );
      const hasStartedFilling = authConfig.headerName?.trim() || 
                               authConfig.headerValue?.trim() || 
                               authConfig.username?.trim() || 
                               authConfig.password?.trim() || 
                               authConfig.clientId?.trim() || 
                               authConfig.clientSecret?.trim() || 
                               authConfig.tokenUrl?.trim() ||
                               hasCustomHeaders;
      
      if (hasStartedFilling || authConfig.type === 'custom') {
        if (authConfig.type === 'api_key') {
          if (!authConfig.headerName?.trim()) {
            newErrors[`auth_${index}_headerName`] = 'Header name is required for API Key authentication';
          }
          if (!authConfig.headerValue?.trim()) {
            newErrors[`auth_${index}_headerValue`] = 'Header value is required for API Key authentication';
          }
        } else if (authConfig.type === 'bearer') {
          if (!authConfig.headerValue?.trim()) {
            newErrors[`auth_${index}_headerValue`] = 'Bearer token is required';
          }
        } else if (authConfig.type === 'basic') {
          if (!authConfig.username?.trim()) {
            newErrors[`auth_${index}_username`] = 'Username is required for Basic authentication';
          }
          if (!authConfig.password?.trim()) {
            newErrors[`auth_${index}_password`] = 'Password is required for Basic authentication';
          }
        } else if (authConfig.type === 'oauth2') {
          if (!authConfig.clientId?.trim()) {
            newErrors[`auth_${index}_clientId`] = 'Client ID is required for OAuth 2.0';
          }
          if (!authConfig.clientSecret?.trim()) {
            newErrors[`auth_${index}_clientSecret`] = 'Client Secret is required for OAuth 2.0';
          }
          if (!authConfig.tokenUrl?.trim()) {
            newErrors[`auth_${index}_tokenUrl`] = 'Token URL is required for OAuth 2.0';
          }
        } else if (authConfig.type === 'custom') {
          const customHeaders = authConfig.customHeaders || [];
          if (customHeaders.length === 0) {
            newErrors[`auth_${index}_customHeaders`] = 'At least one custom header is required';
          } else {
            customHeaders.forEach((header, headerIndex) => {
              if (!header.name?.trim()) {
                newErrors[`auth_${index}_custom_${headerIndex}_name`] = 'Header name is required';
              }
              if (!header.value?.trim()) {
                newErrors[`auth_${index}_custom_${headerIndex}_value`] = 'Header value is required';
              }
            });
          }
        }
      }
    });

    // Only validate rate limit and timeout if user has filled basic info
    if (formData.name.trim() && formData.description.trim() && formData.baseUrl.trim()) {
      if (!formData.rateLimit || formData.rateLimit <= 0) {
        newErrors.rateLimit = 'Rate limit must be greater than 0';
      }

      if (!formData.timeout || formData.timeout <= 0) {
        newErrors.timeout = 'Timeout must be greater than 0';
      }
    }

    // Only validate endpoints if user has filled all basic info and is ready to submit
    if (formData.endpoints.length === 0 && 
        formData.name.trim() && 
        formData.description.trim() && 
        formData.baseUrl.trim() && 
        isValidUrl(formData.baseUrl)) {
      newErrors.endpoints = 'At least one endpoint is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = <K extends keyof APIProvider>(field: K, value: APIProvider[K]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addAuthConfig = () => {
    try {
      const newAuthConfig = {
        type: 'api_key' as const,
        headerName: '',
        headerValue: ''
      };
      
      setFormData(prev => ({
        ...prev,
        authConfigs: [...(prev.authConfigs || []), newAuthConfig]
      }));
      
      // Clear any existing auth-related errors when adding new auth config
      setErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('auth_')) {
            delete newErrors[key];
          }
        });
        // Also clear general auth error
        if (newErrors.auth) {
          delete newErrors.auth;
        }
        return newErrors;
      });
    } catch (error) {
      console.error('Error adding authentication config:', error);
      toast.error('Unable to add authentication method. Please try again.');
    }
  };

  const updateAuthConfig = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      authConfigs: prev.authConfigs.map((config, i) => {
        if (i !== index) return config;

        if (field === 'type' && value === 'custom') {
          return {
            ...config,
            type: 'custom' as const,
            customHeaders: config.customHeaders?.length
              ? config.customHeaders
              : [{ name: '', value: '' }]
          };
        }

        return { ...config, [field]: value };
      })
    }));
  };

  const removeAuthConfig = (index: number) => {
    setFormData(prev => ({
      ...prev,
      authConfigs: prev.authConfigs.filter((_, i) => i !== index)
    }));
  };

  const addCustomHeader = (authIndex: number) => {
    setFormData(prev => ({
      ...prev,
      authConfigs: prev.authConfigs.map((config, i) =>
        i === authIndex
          ? {
              ...config,
              customHeaders: [...(config.customHeaders || []), { name: '', value: '' }]
            }
          : config
      )
    }));
  };

  const updateCustomHeader = (
    authIndex: number,
    headerIndex: number,
    field: 'name' | 'value',
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      authConfigs: prev.authConfigs.map((config, i) => {
        if (i !== authIndex) return config;
        const customHeaders = [...(config.customHeaders || [])];
        customHeaders[headerIndex] = {
          ...customHeaders[headerIndex],
          [field]: value
        };
        return { ...config, customHeaders };
      })
    }));
  };

  const removeCustomHeader = (authIndex: number, headerIndex: number) => {
    setFormData(prev => ({
      ...prev,
      authConfigs: prev.authConfigs.map((config, i) => {
        if (i !== authIndex) return config;
        const customHeaders = (config.customHeaders || []).filter((_, hi) => hi !== headerIndex);
        return {
          ...config,
          customHeaders: customHeaders.length > 0 ? customHeaders : [{ name: '', value: '' }]
        };
      })
    }));
  };

  const addOrUpdateEndpoint = () => {
    if (!endpointPath.trim() || !endpointDescription.trim()) {
      toast.error('Please fill in both path and description for the endpoint');
      return;
    }

    const validParams = endpointParameters.filter(p => p.name.trim() !== '');
    const newEndpoint = {
      path: endpointPath.trim(),
      method: endpointMethod,
      bodyType: ['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? endpointBodyType : 'none',
      description: endpointDescription.trim(),
      parameters: validParams.length > 0 ? validParams : null
    };

    if (editingEndpointIndex !== null) {
      setFormData(prev => ({
        ...prev,
        endpoints: prev.endpoints.map((ep, i) => i === editingEndpointIndex ? newEndpoint : ep)
      }));
      toast.success('Endpoint updated');
      setEditingEndpointIndex(null);
    } else {
      setFormData(prev => ({
        ...prev,
        endpoints: [...prev.endpoints, newEndpoint]
      }));
    }

    // Clear endpoint form
    setEndpointPath('');
    setEndpointMethod('GET');
    setEndpointDescription('');
    setEndpointParameters([]);
    setShowParamsForm(false);

    // Clear endpoint error
    if (errors.endpoints) {
      setErrors(prev => ({
        ...prev,
        endpoints: ''
      }));
    }
  };

  const handleEndpointPathChange = (newPath: string) => {
    setEndpointPath(newPath);

    // Automatically detect dynamic path variables like :tahun or {tahun}
    const extractedNames: string[] = [];
    
    const colonMatches = newPath.matchAll(/(?::([a-zA-Z0-9_]+))/g);
    for (const match of colonMatches) {
      if (match[1] && !extractedNames.includes(match[1])) {
        extractedNames.push(match[1]);
      }
    }
    
    const braceMatches = newPath.matchAll(/(?:\{([a-zA-Z0-9_]+)\})/g);
    for (const match of braceMatches) {
      if (match[1] && !extractedNames.includes(match[1])) {
        extractedNames.push(match[1]);
      }
    }

    if (extractedNames.length > 0) {
      setShowParamsForm(true);
      setEndpointParameters(prev => {
        const updated = [...prev];
        extractedNames.forEach(name => {
          const exists = updated.some(p => p.name === name);
          if (!exists) {
            updated.push({
              name,
              type: 'string',
              location: 'path',
              required: true,
              description: `Path parameter ${name}`
            });
          }
        });
        return updated;
      });
    }
  };

  const removeEndpoint = (index: number) => {
    if (editingEndpointIndex === index) {
      cancelEditEndpoint();
    } else if (editingEndpointIndex !== null && index < editingEndpointIndex) {
      setEditingEndpointIndex(prev => (prev !== null ? prev - 1 : null));
    }
    setFormData(prev => ({
      ...prev,
      endpoints: prev.endpoints.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form data before validation:', formData);
    const isValid = validateForm();
    console.log('Validation result:', isValid);
    console.log('Current errors:', errors);

    if (!isValid) {
      toast.error('Please complete all required fields before submitting');
      return;
    }

    try {
      await onSubmit(formData);
      onClose();
      toast.success(`API Provider ${isEdit ? 'updated' : 'created'} successfully`);
    } catch (error: unknown) {
      console.error('API Provider submission error:', error);
      const msg = error instanceof Error ? error.message : String(error);
      if (msg && msg.includes('Internal server error')) {
        toast.error('Server is temporarily unavailable. Please try again in a few moments.');
      } else if (msg) {
        toast.error(msg);
      } else {
        toast.error(`Unable to ${isEdit ? 'update' : 'create'} API provider. Please check your connection and try again.`);
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9998]"
              onClick={handleClose}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl z-[9999]"
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Server className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {isEdit ? 'Edit API Provider' : 'Add New API Provider'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Configure API provider and its endpoints
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Settings className="w-4 h-4 mr-2" />
                    Basic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Provider Name *
                      </label>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter provider name"
                        error={errors.name}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Base URL *
                      </label>
                      <Input
                        value={formData.baseUrl}
                        onChange={(e) => handleInputChange('baseUrl', e.target.value)}
                        placeholder="https://api.example.com"
                        error={errors.baseUrl}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description *
                    </label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Describe this API provider"
                      rows={3}
                      error={errors.description}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Documentation URL
                    </label>
                    <Input
                      value={formData.documentation || ''}
                      onChange={(e) => handleInputChange('documentation', e.target.value)}
                      placeholder="https://docs.example.com"
                    />
                  </div>
                </div>

                {/* Authentication */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Authentication
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <Switch
                        checked={formData.requiresAuth}
                        onChange={(checked) => handleInputChange('requiresAuth', checked)}
                      />
                      <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                        Requires Authentication
                      </span>
                    </div>
                    
                    {formData.requiresAuth && (
                      <div className="space-y-4">
                        {/* Existing Auth Configs */}
                        {(formData.authConfigs || []).map((authConfig, index) => (
                          <div key={index} className="space-y-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                            <div className="flex items-start space-x-3">
                              {/* Auth Type */}
                              <div className="flex-1 min-w-0">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  Type
                                </label>
                                <Select
                                  value={authConfig.type}
                                  onChange={(e) => updateAuthConfig(index, 'type', e.target.value)}
                                  className="text-sm"
                                >
                                  <option value="api_key">API Keys</option>
                                  <option value="bearer">Bearer Token</option>
                                  <option value="basic">Basic Auth</option>
                                  <option value="oauth2">OAuth 2.0</option>
                                  <option value="custom">Custom Headers</option>
                                </Select>
                              </div>
                              
                              {/* Dynamic fields based on auth type */}
                              {authConfig.type === 'api_key' && (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Header Name
                                    </label>
                                    <Input
                                      value={authConfig.headerName || ''}
                                      onChange={(e) => updateAuthConfig(index, 'headerName', e.target.value)}
                                      placeholder="X-API-Key"
                                      error={errors[`auth_${index}_headerName`]}
                                      className="text-sm"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Value
                                    </label>
                                    <Input
                                      value={authConfig.headerValue || ''}
                                      onChange={(e) => updateAuthConfig(index, 'headerValue', e.target.value)}
                                      placeholder="your-api-key"
                                      type={showValueMap[`auth_${index}_headerValue`] ? 'text' : 'password'}
                                      rightIcon={
                                        <button
                                          type="button"
                                          tabIndex={-1}
                                          onClick={() => toggleShowValue(`auth_${index}_headerValue`)}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                                        >
                                          {showValueMap[`auth_${index}_headerValue`] ? (
                                            <EyeOff className="w-4 h-4" />
                                          ) : (
                                            <Eye className="w-4 h-4" />
                                          )}
                                        </button>
                                      }
                                      error={errors[`auth_${index}_headerValue`]}
                                      className="text-sm"
                                    />
                                  </div>
                                </>
                              )}
                              
                              {authConfig.type === 'bearer' && (
                                <div className="flex-1 min-w-0">
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Bearer Token
                                  </label>
                                  <Input
                                    value={authConfig.headerValue || ''}
                                    onChange={(e) => updateAuthConfig(index, 'headerValue', e.target.value)}
                                    placeholder="your-bearer-token"
                                    type={showValueMap[`auth_${index}_bearer`] ? 'text' : 'password'}
                                    rightIcon={
                                      <button
                                        type="button"
                                        tabIndex={-1}
                                        onClick={() => toggleShowValue(`auth_${index}_bearer`)}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                                      >
                                        {showValueMap[`auth_${index}_bearer`] ? (
                                          <EyeOff className="w-4 h-4" />
                                        ) : (
                                          <Eye className="w-4 h-4" />
                                        )}
                                      </button>
                                    }
                                    className="text-sm"
                                  />
                                </div>
                              )}
                              
                              {authConfig.type === 'basic' && (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Username
                                    </label>
                                    <Input
                                      value={authConfig.username || ''}
                                      onChange={(e) => updateAuthConfig(index, 'username', e.target.value)}
                                      placeholder="username"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Password
                                    </label>
                                    <Input
                                      value={authConfig.password || ''}
                                      onChange={(e) => updateAuthConfig(index, 'password', e.target.value)}
                                      placeholder="password"
                                      type={showValueMap[`auth_${index}_password`] ? 'text' : 'password'}
                                      rightIcon={
                                        <button
                                          type="button"
                                          tabIndex={-1}
                                          onClick={() => toggleShowValue(`auth_${index}_password`)}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                                        >
                                          {showValueMap[`auth_${index}_password`] ? (
                                            <EyeOff className="w-4 h-4" />
                                          ) : (
                                            <Eye className="w-4 h-4" />
                                          )}
                                        </button>
                                      }
                                      className="text-sm"
                                    />
                                  </div>
                                </>
                              )}
                              
                              {authConfig.type === 'oauth2' && (
                                <>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Client ID
                                    </label>
                                    <Input
                                      value={authConfig.clientId || ''}
                                      onChange={(e) => updateAuthConfig(index, 'clientId', e.target.value)}
                                      placeholder="client-id"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Client Secret
                                    </label>
                                    <Input
                                      value={authConfig.clientSecret || ''}
                                      onChange={(e) => updateAuthConfig(index, 'clientSecret', e.target.value)}
                                      placeholder="client-secret"
                                      type={showValueMap[`auth_${index}_clientSecret`] ? 'text' : 'password'}
                                      rightIcon={
                                        <button
                                          type="button"
                                          tabIndex={-1}
                                          onClick={() => toggleShowValue(`auth_${index}_clientSecret`)}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
                                        >
                                          {showValueMap[`auth_${index}_clientSecret`] ? (
                                            <EyeOff className="w-4 h-4" />
                                          ) : (
                                            <Eye className="w-4 h-4" />
                                          )}
                                        </button>
                                      }
                                      className="text-sm"
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Token URL
                                    </label>
                                    <Input
                                      value={authConfig.tokenUrl || ''}
                                      onChange={(e) => updateAuthConfig(index, 'tokenUrl', e.target.value)}
                                      placeholder="https://api.example.com/oauth/token"
                                      className="text-sm"
                                    />
                                  </div>
                                </>
                              )}
                              
                              {/* Remove Button */}
                              <div className="flex-shrink-0">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeAuthConfig(index)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 mt-6"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {authConfig.type === 'custom' && (
                              <div className="space-y-3 pt-1">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Tambahkan satu atau lebih HTTP header custom yang akan dikirim ke provider.
                                </p>
                                {(authConfig.customHeaders || [{ name: '', value: '' }]).map((header, headerIndex) => (
                                  <div key={headerIndex} className="flex items-start space-x-3">
                                    <div className="flex-1 min-w-0">
                                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Header Name
                                      </label>
                                      <Input
                                        value={header.name || ''}
                                        onChange={(e) => updateCustomHeader(index, headerIndex, 'name', e.target.value)}
                                        placeholder="X-Custom-Header"
                                        error={errors[`auth_${index}_custom_${headerIndex}_name`]}
                                        className="text-sm"
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Header Value
                                      </label>
                                      <Input
                                        value={header.value || ''}
                                        onChange={(e) => updateCustomHeader(index, headerIndex, 'value', e.target.value)}
                                        placeholder="header-value"
                                        error={errors[`auth_${index}_custom_${headerIndex}_value`]}
                                        className="text-sm"
                                      />
                                    </div>
                                    <div className="flex-shrink-0">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => removeCustomHeader(index, headerIndex)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 hover:border-red-300 mt-6"
                                        disabled={(authConfig.customHeaders || []).length <= 1}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                                {errors[`auth_${index}_customHeaders`] && (
                                  <p className="text-xs text-red-500">{errors[`auth_${index}_customHeaders`]}</p>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => addCustomHeader(index)}
                                >
                                  <Plus className="w-4 h-4 mr-2" />
                                  Add Header
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Add new auth config button */}
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addAuthConfig}
                          className="w-full"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Authentication Method
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rate Limiting & Timeout */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Zap className="w-4 h-4 mr-2" />
                    Rate Limiting & Timeout
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rate Limit (requests/hour)
                      </label>
                      <Input
                        type="number"
                        value={formData.rateLimit}
                        onChange={(e) => handleInputChange('rateLimit', parseInt(e.target.value))}
                        placeholder="1000"
                        error={errors.rateLimit}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Timeout (milliseconds)
                      </label>
                      <Input
                        type="number"
                        value={formData.timeout}
                        onChange={(e) => handleInputChange('timeout', parseInt(e.target.value))}
                        placeholder="10000"
                        error={errors.timeout}
                      />
                    </div>
                  </div>
                </div>

                {/* Endpoints */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                      <Database className="w-4 h-4 mr-2" />
                      Endpoints ({formData.endpoints.length})
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Format path: <code className="text-blue-600 dark:text-blue-400 font-mono">:id</code>, <code className="text-blue-600 dark:text-blue-400 font-mono">&#123;id&#125;</code>, <code className="text-blue-600 dark:text-blue-400 font-mono">/*</code>, atau Regex
                    </span>
                  </div>
                  
                  {/* Add Endpoint Form */}
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Method
                      </label>
                      <Select
                        value={endpointMethod}
                        onChange={(e) => {
                          const m = e.target.value as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
                          setEndpointMethod(m);
                          if (['POST', 'PUT', 'PATCH'].includes(m)) {
                            setEndpointBodyType('json');
                          } else {
                            setEndpointBodyType('none');
                          }
                        }}
                        className="text-sm"
                      >
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                        <option value="PATCH">PATCH</option>
                      </Select>
                    </div>
                    <div className={['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? 'md:col-span-3' : 'md:col-span-4'}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Path
                      </label>
                      <Input
                        value={endpointPath}
                        onChange={(e) => handleEndpointPathChange(e.target.value)}
                        placeholder="/users/:id atau /users/{id}"
                        className="text-sm font-mono"
                      />
                    </div>
                    {['POST', 'PUT', 'PATCH'].includes(endpointMethod) && (
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Body Format
                        </label>
                        <Select
                          value={endpointBodyType}
                          onChange={(e) => setEndpointBodyType(e.target.value as any)}
                          className="text-sm"
                        >
                          <option value="json">JSON (raw)</option>
                          <option value="form-data">form-data</option>
                          <option value="x-www-form-urlencoded">x-www-form-urlencoded</option>
                          <option value="none">None / No Body</option>
                        </Select>
                      </div>
                    )}
                    <div className={['POST', 'PUT', 'PATCH'].includes(endpointMethod) ? 'md:col-span-2' : 'md:col-span-3'}>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <Input
                        value={endpointDescription}
                        onChange={(e) => setEndpointDescription(e.target.value)}
                        placeholder="Deskripsi endpoint"
                        className="text-sm"
                      />
                    </div>
                    <div className="md:col-span-3 flex items-center space-x-2">
                      <Button
                        type="button"
                        onClick={addOrUpdateEndpoint}
                        className="flex-1 whitespace-nowrap px-3"
                      >
                        {editingEndpointIndex !== null ? (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            Update
                          </>
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </>
                        )}
                      </Button>
                      {editingEndpointIndex !== null && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={cancelEditEndpoint}
                          className="flex-1 whitespace-nowrap px-3"
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Collapsible Parameter Configurator (Matches Endpoint style) */}
                  <div className="mb-4 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        type="button"
                        onClick={() => setShowParamsForm(!showParamsForm)}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center focus:outline-none"
                      >
                        <Settings className="w-3.5 h-3.5 mr-1" />
                        {showParamsForm ? 'Sembunyikan Pengaturan Parameter' : `+ Atur Parameter Query / Body / Path (${endpointParameters.length})`}
                      </button>
                    </div>

                    {showParamsForm && (
                      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-600 space-y-3">
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Daftarkan parameter (seperti <code className="font-mono text-blue-600 dark:text-blue-400">tahun</code>, <code className="font-mono text-blue-600 dark:text-blue-400">kode</code>), tentukan Lokasi (Body/Query) dan Status Wajib / Opsional.
                        </p>

                        {/* Add / Edit Parameter Form */}
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Nama Parameter
                              </label>
                              <Input
                                value={paramName}
                                onChange={(e) => setParamName(e.target.value)}
                                placeholder="e.g. tahun"
                                className="text-sm font-mono"
                              />
                            </div>
                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Tipe Data
                              </label>
                              <Select
                                value={paramType}
                                onChange={(e) => setParamType(e.target.value as any)}
                                className="text-sm"
                              >
                                <option value="string">String (Teks)</option>
                                <option value="number">Number / Integer (Angka)</option>
                                <option value="boolean">Boolean (true/false)</option>
                              </Select>
                            </div>
                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Lokasi
                              </label>
                              <Select
                                value={paramLocation}
                                onChange={(e) => setParamLocation(e.target.value as any)}
                                className="text-sm"
                              >
                                <option value="body">Body Param</option>
                                <option value="query">Query String</option>
                                <option value="header">Header</option>
                                <option value="path">Path Param</option>
                              </Select>
                            </div>
                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Status
                              </label>
                              <Select
                                value={paramRequired ? 'true' : 'false'}
                                onChange={(e) => setParamRequired(e.target.value === 'true')}
                                className="text-sm"
                              >
                                <option value="false">Opsional</option>
                                <option value="true">Wajib (Required)</option>
                              </Select>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-9">
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Deskripsi
                              </label>
                              <Input
                                value={paramDescription}
                                onChange={(e) => setParamDescription(e.target.value)}
                                placeholder="Keterangan parameter"
                                className="text-sm"
                              />
                            </div>
                            <div className="md:col-span-3 flex items-center space-x-2">
                              <Button
                                type="button"
                                onClick={addOrUpdateParam}
                                className="flex-1 whitespace-nowrap px-3"
                              >
                                {editingParamIndex !== null ? (
                                  <>
                                    <Check className="w-4 h-4 mr-1" />
                                    Update
                                  </>
                                ) : (
                                  <>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add
                                  </>
                                )}
                              </Button>
                              {editingParamIndex !== null && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={cancelEditParam}
                                  className="flex-1 whitespace-nowrap px-3"
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Parameters List */}
                        {endpointParameters.length > 0 && (
                          <div className="space-y-2 pt-2">
                            {endpointParameters.map((param, pIdx) => (
                              <div
                                key={pIdx}
                                onClick={() => startEditParam(pIdx)}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors border ${
                                  editingParamIndex === pIdx
                                    ? 'bg-blue-50 border-blue-500 dark:bg-blue-900/30 dark:border-blue-400'
                                    : 'bg-white border-gray-200 dark:bg-gray-600 dark:border-gray-500 hover:border-blue-300 dark:hover:border-blue-400'
                                }`}
                              >
                                <div className="flex items-center space-x-2 min-w-0 flex-1">
                                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded uppercase ${
                                    param.location === 'body' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' :
                                    param.location === 'header' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' :
                                    'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300'
                                  }`}>
                                    {param.location || 'query'}
                                  </span>
                                  <span className="px-2 py-0.5 text-[10px] font-mono rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                                    {param.type || 'string'}
                                  </span>
                                  <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
                                    param.required
                                      ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
                                  }`}>
                                    {param.required ? 'Wajib' : 'Opsional'}
                                  </span>
                                  <div>
                                    <div className="font-mono text-sm font-medium">{param.name}</div>
                                    {param.description && (
                                      <div className="text-xs text-gray-500 dark:text-gray-300">{param.description}</div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEditParam(pIdx);
                                    }}
                                    title="Edit parameter"
                                  >
                                    <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeParam(pIdx);
                                    }}
                                    title="Hapus parameter"
                                  >
                                    <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Endpoints List */}
                  {formData.endpoints.length > 0 ? (
                    <div className="space-y-2">
                      {formData.endpoints.map((endpoint, index) => (
                        <div
                          key={index}
                          onClick={() => startEditEndpoint(index)}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors border ${
                            editingEndpointIndex === index
                              ? 'bg-blue-50 border-blue-500 dark:bg-blue-900/30 dark:border-blue-400'
                              : 'bg-white border-gray-200 dark:bg-gray-600 dark:border-gray-500 hover:border-blue-300 dark:hover:border-blue-400'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                              endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                              endpoint.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                              endpoint.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {endpoint.method}
                            </span>
                            <div>
                              <div className="font-mono text-sm font-medium">{endpoint.path}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-300">{endpoint.description}</div>
                              {endpoint.parameters && endpoint.parameters.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {endpoint.parameters.map((p, pIdx) => (
                                    <span
                                      key={pIdx}
                                      className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                        p.required
                                          ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300'
                                          : 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300'
                                      }`}
                                    >
                                      {p.name} {p.required ? '(Wajib)' : '(Opsional)'}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditEndpoint(index);
                              }}
                              title="Edit endpoint"
                            >
                              <Edit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeEndpoint(index);
                              }}
                              title="Delete endpoint"
                            >
                              <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      No endpoints added yet
                    </div>
                  )}
                  
                  {errors.endpoints && (
                    <p className="text-red-600 text-sm mt-2">{errors.endpoints}</p>
                  )}
                </div>

                {/* Status */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                    <Clock className="w-4 h-4 mr-2" />
                    Status
                  </h4>
                  <div className="flex items-center">
                    <Switch
                      checked={formData.isActive}
                      onChange={(checked) => handleInputChange('isActive', checked)}
                    />
                    <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                      Active
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {isEdit ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <Server className="w-4 h-4 mr-2" />
                        {isEdit ? 'Update Provider' : 'Create Provider'}
                      </div>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
} 
